// Microbe RTS — custom battlefield ICONOGRAPHY (canvas paths, not font emoji).
//
// Every node type and unit kind gets a hand-drawn pictograph so nothing depends on the platform
// emoji/glyph font (which renders differently per device and can't be colored/scaled cleanly).
// These are tiny, bold, single-color marks drawn centered at the current transform origin — the
// caller has already translated to the node/unit centre and picks the color via `col`.

import type { NodeType, UnitKind } from '../types.js';

const TAU = Math.PI * 2;

/** Draw a node-type pictograph, centered, sized to radius `s`, in color `col`. */
export function drawNodeIcon(g: CanvasRenderingContext2D, type: NodeType, s: number, col: string): void {
  g.save();
  g.strokeStyle = col;
  g.fillStyle = col;
  g.lineWidth = Math.max(1.4, s * 0.16);
  g.lineCap = 'round';
  g.lineJoin = 'round';
  switch (type) {
    case 'home': {
      // Colony nucleus: a solid core ringed by three spores.
      g.beginPath();
      g.arc(0, 0, s * 0.34, 0, TAU);
      g.fill();
      for (let i = 0; i < 3; i++) {
        const a = -Math.PI / 2 + (i / 3) * TAU;
        g.beginPath();
        g.arc(Math.cos(a) * s * 0.72, Math.sin(a) * s * 0.72, s * 0.15, 0, TAU);
        g.fill();
      }
      break;
    }
    case 'reef': {
      // Three coral branches forking at their tips.
      for (let i = 0; i < 3; i++) {
        const a = -Math.PI / 2 + (i / 3) * TAU;
        const tx = Math.cos(a) * s * 0.85;
        const ty = Math.sin(a) * s * 0.85;
        g.beginPath();
        g.moveTo(0, 0);
        g.lineTo(tx * 0.7, ty * 0.7);
        g.stroke();
        const px = -Math.sin(a);
        const py = Math.cos(a);
        g.beginPath();
        g.moveTo(tx * 0.7, ty * 0.7);
        g.lineTo(tx + px * s * 0.22, ty + py * s * 0.22);
        g.moveTo(tx * 0.7, ty * 0.7);
        g.lineTo(tx - px * s * 0.22, ty - py * s * 0.22);
        g.stroke();
      }
      break;
    }
    case 'vent': {
      // Chimney base + three rising bubbles.
      g.beginPath();
      g.moveTo(-s * 0.42, s * 0.85);
      g.lineTo(-s * 0.24, s * 0.1);
      g.lineTo(s * 0.24, s * 0.1);
      g.lineTo(s * 0.42, s * 0.85);
      g.stroke();
      for (let i = 0; i < 3; i++) {
        g.beginPath();
        g.arc((i - 1) * s * 0.28, -s * (0.2 + i * 0.28), s * 0.13, 0, TAU);
        g.fill();
      }
      break;
    }
    case 'light': {
      // Sun: core + rays.
      g.beginPath();
      g.arc(0, 0, s * 0.34, 0, TAU);
      g.fill();
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * TAU;
        g.beginPath();
        g.moveTo(Math.cos(a) * s * 0.5, Math.sin(a) * s * 0.5);
        g.lineTo(Math.cos(a) * s * 0.9, Math.sin(a) * s * 0.9);
        g.stroke();
      }
      break;
    }
    case 'toxin': {
      // Trefoil hazard: three lobes around a hub.
      g.beginPath();
      g.arc(0, 0, s * 0.16, 0, TAU);
      g.fill();
      for (let i = 0; i < 3; i++) {
        const a = -Math.PI / 2 + (i / 3) * TAU;
        g.beginPath();
        g.arc(Math.cos(a) * s * 0.52, Math.sin(a) * s * 0.52, s * 0.28, 0, TAU);
        g.stroke();
      }
      break;
    }
  }
  g.restore();
}

/** Draw a unit-kind pictograph, centered, sized to radius `s`, in color `col`. */
export function drawUnitIcon(g: CanvasRenderingContext2D, kind: UnitKind, s: number, col: string): void {
  g.save();
  g.strokeStyle = col;
  g.fillStyle = col;
  g.lineWidth = Math.max(1.3, s * 0.2);
  g.lineCap = 'round';
  g.lineJoin = 'round';
  switch (kind) {
    case 'lancer': {
      // Lance: shaft + arrow tip.
      g.beginPath();
      g.moveTo(-s * 0.5, s * 0.5);
      g.lineTo(s * 0.5, -s * 0.5);
      g.moveTo(s * 0.5, -s * 0.5);
      g.lineTo(s * 0.12, -s * 0.5);
      g.moveTo(s * 0.5, -s * 0.5);
      g.lineTo(s * 0.5, -s * 0.12);
      g.stroke();
      break;
    }
    case 'phalanx': {
      // Shield.
      g.beginPath();
      g.moveTo(0, -s * 0.6);
      g.lineTo(s * 0.55, -s * 0.32);
      g.lineTo(s * 0.4, s * 0.4);
      g.lineTo(0, s * 0.65);
      g.lineTo(-s * 0.4, s * 0.4);
      g.lineTo(-s * 0.55, -s * 0.32);
      g.closePath();
      g.stroke();
      break;
    }
    case 'stinger': {
      // Arrow (ranged): shaft + head + fletch.
      g.beginPath();
      g.moveTo(-s * 0.55, s * 0.55);
      g.lineTo(s * 0.55, -s * 0.55);
      g.moveTo(s * 0.55, -s * 0.55);
      g.lineTo(s * 0.15, -s * 0.5);
      g.moveTo(s * 0.55, -s * 0.55);
      g.lineTo(s * 0.5, -s * 0.15);
      g.moveTo(-s * 0.55, s * 0.55);
      g.lineTo(-s * 0.2, s * 0.5);
      g.moveTo(-s * 0.55, s * 0.55);
      g.lineTo(-s * 0.5, s * 0.2);
      g.stroke();
      break;
    }
    case 'engulfer': {
      // Devouring maw: a thick crescent.
      g.beginPath();
      g.arc(0, 0, s * 0.55, Math.PI * 0.28, Math.PI * 1.72);
      g.stroke();
      g.beginPath();
      g.arc(s * 0.12, -s * 0.08, s * 0.08, 0, TAU);
      g.fill();
      break;
    }
    case 'toxifer': {
      // Venom droplet.
      g.beginPath();
      g.moveTo(0, -s * 0.6);
      g.bezierCurveTo(s * 0.55, s * 0.05, s * 0.35, s * 0.6, 0, s * 0.6);
      g.bezierCurveTo(-s * 0.35, s * 0.6, -s * 0.55, s * 0.05, 0, -s * 0.6);
      g.closePath();
      g.stroke();
      break;
    }
    case 'drone': {
      // Watchful eye (its role is vision).
      g.beginPath();
      g.moveTo(-s * 0.65, 0);
      g.quadraticCurveTo(0, -s * 0.55, s * 0.65, 0);
      g.quadraticCurveTo(0, s * 0.55, -s * 0.65, 0);
      g.closePath();
      g.stroke();
      g.beginPath();
      g.arc(0, 0, s * 0.2, 0, TAU);
      g.fill();
      break;
    }
    case 'healer': {
      // Medical cross.
      g.beginPath();
      g.moveTo(0, -s * 0.6);
      g.lineTo(0, s * 0.6);
      g.moveTo(-s * 0.6, 0);
      g.lineTo(s * 0.6, 0);
      g.stroke();
      break;
    }
    case 'grazer': {
      // Heavy fauna: a bold nucleus ring with a solid core.
      g.beginPath();
      g.arc(0, 0, s * 0.6, 0, TAU);
      g.stroke();
      g.beginPath();
      g.arc(0, 0, s * 0.24, 0, TAU);
      g.fill();
      break;
    }
  }
  g.restore();
}
